import { bridge } from './bridge';

/**
 * 화면마다 행 배열이 담긴 state 키가 다르다 (data, leftData, dataMonth …).
 * 어느 키인지는 카탈로그의 rowsStateKey 가 알고 있고, 여기서는 받아 쓴다.
 */
export function readRows(rowsStateKey: string): Record<string, unknown>[] {
  const state = bridge().pageState();
  if (!state) throw new Error('열린 화면이 없다');
  if (!(rowsStateKey in state)) {
    throw new Error(`화면 상태에 ${rowsStateKey} 가 없다. 카탈로그가 오래됐을 수 있다`);
  }
  const rows = state[rowsStateKey];
  // **한 건짜리 양식은 한 행이다.** 회사정보등록은 표가 없고 `data` 에 객체 하나를
  // 둔다. 카탈로그가 그것을 `kind: 'record'` 표로 올린다(2026-09-23).
  if (rows && typeof rows === 'object' && !Array.isArray(rows)) {
    return [rows as Record<string, unknown>];
  }
  if (!Array.isArray(rows)) throw new Error(`${rowsStateKey} 가 배열이 아니다`);
  return (rows as Record<string, unknown>[]).map(unwrapCalendarRow);
}

/**
 * 달력 화면의 행은 **한 겹 싸여 있다** — `{ date, data: { …실제 값 } }`.
 *
 * 그대로 내보내면 계산이 `row["actnm"]` 을 찾다 못 찾는다. 날짜를 살린 채 한 겹 벗긴다.
 * 다른 화면의 행이 우연히 이 모양일 일은 없다 — 키가 정확히 둘이고 `data` 가 객체다.
 */
function unwrapCalendarRow(row: Record<string, unknown>): Record<string, unknown> {
  if (!row || typeof row !== 'object') return row;
  const keys = Object.keys(row);
  if (keys.length !== 2 || !keys.includes('date') || !keys.includes('data')) return row;
  const inner = row.data;
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) return row;
  return { date: row.date, ...(inner as Record<string, unknown>) };
}

/**
 * 화면이 이미 계산해 둔 값을 걷는다.
 *
 * 지도가 실제 화면과 어긋날 수 있으므로 없는 키는 조용히 뺀다.
 */
export function readScalars(keys: string[]): Record<string, unknown> {
  const state = bridge().pageState() ?? {};
  const picked: Record<string, unknown> = {};
  for (const key of keys) {
    const value = state[key];
    if (value === undefined || Array.isArray(value)) continue;
    if (value !== null && typeof value === 'object') continue;
    picked[key] = value;
  }
  return picked;
}

/**
 * 조회 전후로 참조가 바뀐 배열 키를 긴 것부터 돌려준다.
 *
 * 카탈로그는 284개 화면 중 225개에서만 행 키를 안다. 탭 화면은 키를 런타임에
 * 고르기 때문에 정적으로 알 방법이 없다. 그런 화면에서는 "조회하니 무엇이
 * 바뀌었나" 가 결과가 어디 있는지 알려준다.
 *
 * 합계나 요약 배열이 같이 바뀌기도 한다. 행이 많은 쪽이 결과 그리드다.
 */
export function changedArrays(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  if (!after) return [];
  const keys: string[] = [];
  for (const [key, value] of Object.entries(after)) {
    if (!Array.isArray(value)) continue;
    if (before && before[key] === value) continue;
    keys.push(key);
  }
  return keys.sort((a, b) => (after[b] as unknown[]).length - (after[a] as unknown[]).length);
}
